import { Box, IconButton, SxProps } from '@mui/material'
import { Position } from '@xyflow/react'
import { Handle, useReactFlow } from '@xyflow/react'
import CloseIcon from '@mui/icons-material/Close'
import NodeHeader from './NodeHeader'
import { useFlowState } from '../../FlowStateContext'
import { useAppTheme } from '@/theme/ThemeContext'

const NodeBox = ({
  title,
  icon,
  children,
  sx,
  nodeId,
  endsBranch = false,
}: {
  title: string
  icon: string
  children: React.ReactNode
  sx?: SxProps
  // The React Flow node id, so the box can delete itself. Omitted by any
  // non-canvas usage, which simply gets no delete button.
  nodeId?: string
  // The send node and the end node end a branch: no bottom connector, so
  // nothing can be connected below them.
  endsBranch?: boolean
}) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const { deleteElements } = useReactFlow()
  const { rootId } = useFlowState()
  // The root is the tree's entry point — deleting it would leave nothing to
  // serialize from, so it has no ✕ (the canvas also vetoes the Delete key).
  const deletable = !!nodeId && nodeId !== rootId
  // Nothing connects above the root, so it draws no top connector.
  const isRoot = !!nodeId && nodeId === rootId

  const colors = {
    bg: isDarkMode ? '#162645' : '#FAFBFD',
    border: isDarkMode ? '#445876' : 'rgba(50, 59, 119, 0.10)',
  }

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '8px',
        border: `1px solid ${colors.border}`,
        background: colors.bg,
        ...sx,
      }}
    >
      {deletable && (
        // Top-LEFT: titles sit right in this RTL UI, so the left corner is the
        // free one. `nodrag` is mandatory — without it the mousedown starts a
        // node drag and the click never fires (see the node commons).
        <IconButton
          aria-label="מחק רכיב"
          className="nodrag"
          size="small"
          onClick={() => deleteElements({ nodes: [{ id: nodeId }] })}
          sx={{ position: 'absolute', top: 2, left: 2, zIndex: 1 }}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
      )}
      <NodeHeader title={title} icon={icon} />
      <Box sx={{ padding: '16px' }}>{children}</Box>

      {!isRoot && <Handle type="target" position={Position.Top} />}
      {!endsBranch && <Handle type="source" position={Position.Bottom} />}
    </Box>
  )
}

export default NodeBox
